import { createFileRoute, notFound } from "@tanstack/react-router";
import ProductPage from "@/pages/ProductPage";
import { buildHead, canonicalUrl } from "@/lib/seo/head";
import { staticCountry, countryLang } from "@/lib/seo/country";
import { productAlternates } from "@/lib/seo/hreflang";
import { getSupabase } from "@/integrations/supabase/lazy";
import { fetchFrameDetail, frameDetailQueryKey } from "@/hooks/useFrameDetail";
import { createMemo } from "@/lib/memoCache";

type FrameHeadRow = {
  name: string | null;
  name_en: string | null;
  description: string | null;
  description_en: string | null;
  base_image_url: string | null;
};

const productHeadMemo = createMemo<{ frame: FrameHeadRow | null; ids: string[] }>(
  60 * 60 * 1000,
  3000,
);

interface ProductHeadData {
  name: string | null;
  nameEn: string | null;
  description: string | null;
  descriptionEn: string | null;
  image: string | null;
  /** Slugs des pays où la monture est réellement disponible. */
  availableCountries: string[];
}

export const Route = createFileRoute("/$country/product/$handle")({
  component: ProductPage,
  loader: async ({ params, context }): Promise<ProductHeadData> => {
    const supabase = await getSupabase();
    // Prime le détail monture côté serveur : la page produit peut alors rendre
    // son JSON-LD (Product/Offer, Breadcrumb, FAQ) dès le HTML initial.
    const { data: countryRows } = await supabase
      .from("countries")
      .select("id, slug");

    const countries = (countryRows ?? []) as { id: string; slug: string }[];
    const countryId = countries.find((c) => c.slug === params.country)?.id;
    if (countryId) {
      await context.queryClient
        .ensureQueryData({
          queryKey: frameDetailQueryKey(params.handle, countryId),
          queryFn: () => fetchFrameDetail(params.handle, countryId),
          staleTime: 5 * 60 * 1000,
        })
        .catch(() => null);
    }

    // Mis en cache 1 h par monture : ces deux lectures étaient refaites à
    // chaque affichage (robots compris). Une erreur base n'est pas cachée.
    let data: FrameHeadRow | null = null;
    let error: unknown = null;
    let availableIds = new Set<string>();
    try {
      const cached = await productHeadMemo(params.handle, async () => {
        const res = await supabase
          .from("frames")
          .select("name, name_en, description, description_en, base_image_url")
          .eq("slug", params.handle)
          .eq("is_active", true)
          .maybeSingle();
        if (res.error) throw res.error;
        let ids: string[] = [];
        if (res.data) {
          // Marchés où la monture est en vente : sert à déclarer le cluster
          // hreflang (sinon Google fusionne les URL pays quasi identiques).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: availabilityRows } = await (supabase.from as any)(
            "country_catalog",
          )
            .select("country_id")
            .eq("slug", params.handle)
            .eq("is_available", true);
          ids = ((availabilityRows ?? []) as { country_id: string | null }[])
            .map((r) => r.country_id)
            .filter((id): id is string => Boolean(id));
        }
        return { frame: res.data as FrameHeadRow | null, ids };
      });
      data = cached.frame;
      availableIds = new Set(cached.ids);
    } catch (e) {
      error = e;
    }

    // Monture inexistante (ou désactivée) : vraie 404. Sans ça chaque SKU
    // absent d'un pays produisait une page 200 « Produit introuvable »,
    // identique d'un pays à l'autre → doublons dans Search Console.
    // Une erreur base n'est PAS une absence : on laisse la page se rendre.
    if (!error && !data) throw notFound();
    const availableCountries = countries
      .filter((c) => availableIds.has(c.id))
      .map((c) => c.slug);

    return {
      name: data?.name ?? null,
      nameEn: (data as { name_en?: string | null } | null)?.name_en ?? null,
      description: data?.description ?? null,
      descriptionEn:
        (data as { description_en?: string | null } | null)?.description_en ?? null,
      image: data?.base_image_url ?? null,
      availableCountries,
    };
  },


  head: ({ params, loaderData }) => {
    const country = staticCountry(params.country);
    const lang = countryLang(params.country);
    const canonical = canonicalUrl(`/${params.country}/product/${params.handle}`);
    const countryName = country?.name ?? "";
    const preposition = country?.preposition ?? "";

    // loaderData absent = notFound() (ou loader interrompu) : ni index ni
    // canonical, pour ne pas déclarer une page d'erreur comme URL de référence.
    if (!loaderData) {
      return buildHead({
        title: lang === "fr" ? "Produit introuvable | Lapaire" : "Product not found | Lapaire",
        description:
          lang === "fr"
            ? "Cette monture n'est plus disponible. Découvrez le catalogue Lapaire."
            : "This frame is no longer available. Browse the Lapaire catalogue.",
        canonical,
        lang,
        robots: "noindex, follow",
        noCanonical: true,
      });
    }



    const frameName =
      (lang === "en" ? loaderData?.nameEn : loaderData?.name) ??
      loaderData?.name ??
      null;
    const frameDescription =
      (lang === "en" ? loaderData?.descriptionEn : loaderData?.description) ??
      loaderData?.description ??
      null;

    const title = frameName
      ? lang === "fr"
        ? `${frameName} — Lunettes ${preposition} ${countryName} | Lapaire`
        : `${frameName} — Glasses in ${countryName} | Lapaire`
      : lang === "fr"
        ? `Lunettes ${preposition} ${countryName} | Lapaire`
        : `Glasses in ${countryName} | Lapaire`;

    const description =
      frameDescription ??
      (lang === "fr"
        ? `Découvrez ${frameName ?? "nos lunettes"} Lapaire ${preposition} ${countryName}. Test de vue gratuit en magasin.`
        : `Discover ${frameName ?? "Lapaire glasses"} in ${countryName}. Free eye test in-store.`);

    // Cluster hreflang inter-pays : les URL produit des autres marchés sont des
    // variantes régionales, pas des doublons.
    const available = loaderData.availableCountries ?? [];
    const alternates = productAlternates(params.handle, available);

    // Monture pas en vente dans ce pays : la page existe (catalogue partagé)
    // mais elle n'a rien d'unique à indexer → noindex, follow.
    const unavailableHere =
      available.length > 0 && !available.includes(params.country);

    return buildHead({
      title,
      description,
      canonical,
      lang,
      ogType: "website",
      ...(alternates.length > 0 ? { alternates } : {}),
      ...(unavailableHere ? { robots: "noindex, follow" } : {}),
      ...(loaderData?.image ? { ogImage: loaderData.image } : {}),
    });

  },
});
